import { priceImpact } from '../shared/priceImpact';
import { useI18n } from "./i18n";
import { priceLine } from "../shared/domain";
import {
  catalogChanges,
  validationEditorTarget,
  preparationGroups,
  changeSelection,
  selectionKey,
  changedFields,
  changeKind,
  groupedChangeCounts,
  type CatalogChange,
  type CatalogChangeGroup,
} from "./adminUx";
export function useAdminCopy() {
  const { locale } = useI18n();
  return (en: string, id: string) => (locale === "id" ? id : en);
}
const labels: Record<string, [string, string]> = {
  coffee: ["Coffee profile", "Profil kopi"],
  filterPortions: ["Filter portions", "Porsi filter"],
  filterSelection: ["Filter bean selection", "Pilihan biji filter"],
  packGrams: ["Pack weight (g)", "Berat kemasan (g)"],
  doseGrams: ["Dose (g)", "Dosis (g)"],
  portionsPerPack: ["Portions per pack", "Porsi per kemasan"],
  roaster: ["Roaster", "Penyangrai"],
  origin: ["Origin", "Asal"],
  process: ["Process", "Proses"],
  roastLevel: ["Roast level", "Tingkat sangrai"],
  tastingNotes: ["Tasting notes", "Catatan rasa"],
  variants: ["Temperature recipes", "Resep suhu"],
  hot: ["Hot", "Panas"],
  iced: ["Iced", "Dingin"],
  espresso: ["Espresso", "Espresso"],
  manual: ["Manual brew", "Seduh manual"],
  machine: ["Machine / brewer", "Mesin / alat seduh"],
  grinder: ["Grinder", "Penggiling"],
  grindSetting: ["Grind setting", "Pengaturan gilingan"],
  temperatureC: ["Temperature (°C)", "Suhu (°C)"],
  targetYieldG: ["Target output (g)", "Target hasil (g)"],
  timeMinSeconds: ["Minimum time (s)", "Waktu minimum (d)"],
  timeMaxSeconds: ["Maximum time (s)", "Waktu maksimum (d)"],
  timerStart: ["Start timer at", "Mulai timer saat"],
  brewByWeight: ["Brew-by-weight", "Brew-by-weight"],
  machineWeightG: ["Machine weight setting (g)", "Pengaturan berat mesin (g)"],
  notes: ["Notes", "Catatan"],
  brewingProfiles: ["Brewing profiles", "Profil seduh"],
  preparationSteps: ["Preparation steps", "Langkah pembuatan"],
  brewMethod: ["Brewing method", "Metode seduh"],
  name: ["Name", "Nama"],
  description: ["Description", "Deskripsi"],
  imageUrl: ["Photo", "Foto"],
  category: ["Category", "Kategori"],
  ingredients: ["Ingredients", "Bahan"],
  equipmentIds: ["Equipment", "Peralatan"],
  packaging: ["Packaging", "Kemasan"],
  beanIds: ["Bean choices", "Pilihan biji"],
  iceLevels: ["Ice choices", "Pilihan es"],
  iceQuantities: ["Ice quantities", "Takaran es"],
  sweetnessLevels: ["Sweetness", "Tingkat manis"],
  extras: ["Extras", "Tambahan"],
  defaults: ["Defaults", "Pilihan default"],
  publicSurcharge: ["Public surcharge", "Tambahan harga publik"],
  costingProfile: ["Costing profile", "Profil biaya"],
  espressoOverheadPercent: ["Bean overhead (%)", "Overhead biji (%)"],
  available: ["Available", "Tersedia"],
  active: ["Active", "Aktif"],
  archived: ["Archived", "Diarsipkan"],
  purchaseCost: ["Purchase cost", "Harga beli"],
  purchaseQuantity: ["Purchase quantity", "Jumlah beli"],
  purchaseUnit: ["Purchase unit", "Satuan beli"],
  usableYield: ["Usable yield (%)", "Hasil pakai (%)"],
  cost: ["Unit cost", "Biaya per unit"],
  fee: ["Equipment fee", "Biaya alat"],
  paused: ["Ordering paused", "Pesanan dijeda"],
  openingHours: ["Opening hours", "Jam operasional"],
  pickupInstructions: ["Pickup instructions", "Petunjuk pengambilan"],
  ingredientId: ["Ingredient", "Bahan"],
  quantity: ["Quantity", "Jumlah"],
  unit: ["Unit", "Satuan"],
  role: ["Role", "Peran"],
  packagingId: ["Packaging", "Kemasan"],
  beanId: ["Bean", "Biji"],
  ice: ["Ice", "Es"],
  sweetness: ["Sweetness", "Tingkat manis"],
  day: ["Day", "Hari"],
  open: ["Open", "Buka"],
  close: ["Close", "Tutup"],
  closed: ["Closed", "Tutup"],
};
const groupOrder: CatalogChangeGroup[] = ["menu", "costs", "settings"];
function ChangeKindBadge({ change }: { change: CatalogChange }) {
  const copy = useAdminCopy();
  const kind = changeKind(change);
  const text = {
    added: copy("Added", "Ditambahkan"),
    updated: copy("Updated", "Diperbarui"),
    archived: copy("Archived", "Diarsipkan"),
    removed: copy("Removed", "Dihapus"),
  }[kind];
  return <span className={`change-kind ${kind}`}>{text}</span>;
}
function useChangeLabels() {
  const copy = useAdminCopy();
  const { t } = useI18n();
  const label = (key: string) => (labels[key] ? copy(...labels[key]) : key);
  const title = (change: CatalogChange) => {
    if (change.collection !== "settings") return change.name;
    return (
      {
        espressoOverheadPercent: t("field.espressoOverhead"),
        name: t("admin.setting.stationName"),
        publicSurcharge: t("admin.setting.surcharge"),
        paused: t("admin.setting.orderStatus"),
        pickupInstructions: t("admin.setting.pickup"),
        openingHours: t("admin.setting.hours"),
      } as Record<string, string>
    )[change.name] || label(change.name);
  };
  const groupTitle = (group: CatalogChangeGroup) =>
    group === "menu"
      ? copy("Menu", "Menu")
      : group === "costs"
        ? copy("Recipes & supplies", "Resep & bahan")
        : copy("Station settings", "Pengaturan kedai");
  return { label, title, groupTitle };
}
export function DraftChangeSummary({
  changes,
  onReview,
  onOpenChange,
  onDiscardChange,
  busy = false,
}: {
  changes: CatalogChange[];
  onReview: () => void;
  onOpenChange: (change: CatalogChange) => void;
  onDiscardChange: (change: CatalogChange) => void;
  busy?: boolean;
}) {
  const copy = useAdminCopy();
  const { label, title, groupTitle } = useChangeLabels();
  const counts = groupedChangeCounts(changes);
  return (
    <div className="draft-status">
      <div className="draft-status-heading">
        <div>
          <strong role="status">
            {changes.length
              ? copy(
                  `${changes.length} ${changes.length === 1 ? "change" : "changes"} not yet live`,
                  `${changes.length} perubahan belum aktif`,
                )
              : copy(
                  "Saved draft matches the live station",
                  "Draf tersimpan sama dengan kedai aktif",
                )}
          </strong>
          <p>
            {copy(
              "Choose which catalog and station changes to save to the live station. Staff access changes apply immediately.",
              "Pilih perubahan katalog dan kedai yang akan disimpan ke kedai aktif. Perubahan akses staf langsung berlaku.",
            )}
          </p>
        </div>
        {changes.length > 0 && (
          <button className="secondary" onClick={onReview} disabled={busy}>
            {copy("Save changes", "Simpan perubahan")}
          </button>
        )}
      </div>
      {changes.length > 0 && (
        <>
          <div className="draft-counts" aria-label={copy("Changes by area", "Perubahan per bagian")}>
            {groupOrder.map((group) => (
              <span key={group}>
                {groupTitle(group)} <strong>{counts[group]}</strong>
              </span>
            ))}
          </div>
          <p className="draft-count-help">
            {copy(
              "Counts are changed items or settings; field names show what changed.",
              "Jumlah menunjukkan item atau pengaturan yang berubah; nama kolom menunjukkan perubahannya.",
            )}
          </p>
          <div className="draft-change-groups">
            {groupOrder.map((group) => {
              const rows = changes.filter((change) => change.group === group);
              return (
                <details className="draft-change-group" key={group}>
                  <summary>
                    {groupTitle(group)} <span>{rows.length}</span>
                  </summary>
                  {rows.length ? (
                    <ul>
                      {rows.map((change, index) => {
                        const fields = changedFields(change);
                        return (
                          <li key={`${change.collection}-${change.name}-${index}`}>
                            <div>
                              <strong>{title(change)}</strong> <ChangeKindBadge change={change} />
                              <small>
                                {fields.slice(0, 3).map(label).join(", ")}
                                {fields.length > 3
                                  ? copy(
                                      ` +${fields.length - 3} more`,
                                      ` +${fields.length - 3} lainnya`,
                                    )
                                  : ""}
                              </small>
                            </div>
                            <div className="draft-change-actions">
                              <button className="text-button" disabled={busy} onClick={() => onOpenChange(change)}>
                                {change.after
                                  ? copy("Open editor", "Buka editor")
                                  : copy("Open section", "Buka bagian")}
                              </button>
                              <button className="text-button discard-change" disabled={busy}
                                aria-label={copy(`Discard changes for ${title(change)}`, `Buang perubahan untuk ${title(change)}`)}
                                onClick={() => onDiscardChange(change)}>
                                {copy("Discard", "Buang")}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>{copy("No changes in this area.", "Tidak ada perubahan di bagian ini.")}</p>
                  )}
                </details>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
export function DraftReview({
  catalog,
  published,
  errors = [],
  onOpenChange,
  canOpenValidation,
  onOpenValidation,
}: any) {
  const copy = useAdminCopy();
  const { money, translateMessage, translateOptionLabel, t } = useI18n();
  const { label, title, groupTitle } = useChangeLabels();
  const reference = (v: string) =>
    ["ingredients", "packaging", "equipment", "products"]
      .flatMap((k) => [...(catalog?.[k] || []), ...(published?.[k] || [])])
      .find((r: any) => r.id === v)?.name || translateOptionLabel(v);
  const format = (value: any): string => {
    if (value == null) return "—";
    if (typeof value === "boolean")
      return value ? copy("Yes", "Ya") : copy("No", "Tidak");
    if (Array.isArray(value))
      return value.length ? value.map(format).join("; ") : "—";
    if (typeof value === "object")
      return Object.entries(value)
        .filter(([k]) => k !== "id" && k !== "updatedAt")
        .map(
          ([k, v]) => `${label(k)}: ${k === "day" ? t(`day.${v}`) : format(v)}`,
        )
        .join(", ");
    return typeof value === "string"
      ? (
          {
            pump: copy("Pump activation", "Pompa menyala"),
            first_drip: copy("First drip", "Tetes pertama"),
            espresso: copy("Espresso — shared allowance", "Espresso — alokasi bersama"),
            manual: copy("Manual brew / other", "Seduh manual / lainnya"),
            legacy: copy("Retained exception", "Pengecualian dipertahankan"),
            none: t("option.noIce"),
            less: t("option.lessIce"),
            normal: t("option.normalIce"),
            extra: t("option.extraIce"),
            own_cup: t("option.ownCup"),
            takeaway: t("option.takeaway"),
            bean: copy("Bean choice", "Pilihan biji"),
            sweetener: copy("Sweetener", "Pemanis"),
          } as Record<string, string>
        )[value] || reference(value)
      : String(value);
  };
  const changes = catalogChanges(catalog, published);
  const priceChanges = priceImpact(published, catalog);
  const renderPrice = (row: ReturnType<typeof priceImpact>[number], i: number) => <article key={`${row.productId}-${i}`}>
    <strong>{row.name} · {row.defaultCup ? copy('Default cup', 'Pilihan bawaan') : row.choices.map(choice => choice.startsWith('ice:') ? t(`option.${choice.slice(4) === 'none' ? 'no' : choice.slice(4)}Ice`) : choice === 'own_cup' ? t('option.ownCup') : choice === 'takeaway' ? t('option.takeaway') : translateOptionLabel(choice)).join(' + ')}</strong>
    {['employee', 'public'].map((channel, index) => <div className="change-row" key={channel}>
      <span>{channel === 'employee' ? copy('Employee', 'Karyawan') : copy('Public', 'Publik')}</span>
      <del>{row.before[index] === null ? '—' : money(row.before[index]!)}</del><span>→</span><ins>{row.after[index] === null ? '—' : money(row.after[index]!)}</ins>
    </div>)}
  </article>;
  return (
    <div className="draft-review">
      {errors.length > 0 && (
        <section className="validation-blockers" role="alert">
          <h3>{copy("Selected changes need attention", "Perubahan terpilih perlu diperiksa")}</h3>
          <p>
            {copy(
              "Fix these issues, select required dependencies, or deselect the affected changes.",
              "Perbaiki kendala ini, pilih kebutuhan terkait, atau batalkan pilihan perubahan bermasalah.",
            )}
          </p>
          <ul>
            {errors.map((error: string, index: number) => (
              <li key={index}>
                <span>{translateMessage(error)}</span>
                {canOpenValidation?.(error) && (
                  <button className="text-button" onClick={() => onOpenValidation(error)}>
                    {copy("Open relevant editor", "Buka editor terkait")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <p>
        {copy(
          "Compare current values → values after saving.",
          "Bandingkan nilai saat ini → nilai setelah disimpan.",
        )}
      </p>
      {groupOrder.map((group) => {
        const rows = changes.filter((x) => x.group === group);
        return (
          rows.length > 0 && (
            <section key={group}>
              <h3>
                {groupTitle(group)} · {rows.length}
              </h3>
              {rows.map((change, i) => (
                <article key={i}>
                  <div className="change-heading">
                    <strong>{title(change)}</strong>
                    <ChangeKindBadge change={change} />
                    {onOpenChange && (
                      <button className="text-button" onClick={() => onOpenChange(change)}>
                        {change.after
                          ? copy("Open editor", "Buka editor")
                          : copy("Open section", "Buka bagian")}
                      </button>
                    )}
                  </div>
                  {changedFields(change).map((field) => (
                    <div className="change-row" key={field}>
                      <span>{label(field)}</span>
                      <del>
                        {format(
                          change.collection === "settings"
                            ? change.before
                            : change.before?.[field],
                        )}
                      </del>
                      <span aria-label={copy("Changes to", "Berubah menjadi")}>
                        →
                      </span>
                      <ins>
                        {format(
                          change.collection === "settings"
                            ? change.after
                            : change.after?.[field],
                        )}
                      </ins>
                    </div>
                  ))}
                </article>
              ))}
            </section>
          )
        );
      })}
      {priceChanges.length > 0 && <section>
        <h3>{copy('Price changes', 'Perubahan harga')}</h3>
        {priceChanges.filter(row => row.defaultCup).map(renderPrice)}
        {priceChanges.some(row => !row.defaultCup) && <details className="option-price-impact">
          <summary>{copy(`Option prices · ${priceChanges.filter(row => !row.defaultCup).length} changed examples`, `Harga opsi · ${priceChanges.filter(row => !row.defaultCup).length} contoh berubah`)}</summary>
          <p>{copy('One option changed from the default cup, plus bean/extra pairs. These examples include added and removed choices; they do not enumerate every combination. — means the choice cannot be priced.', 'Satu opsi diubah dari pilihan bawaan, ditambah pasangan biji/tambahan. Contoh mencakup opsi baru dan dihapus, bukan setiap kombinasi. — berarti opsi tidak dapat dihitung.')}</p>
          {priceChanges.filter(row => !row.defaultCup).map(renderPrice)}
        </details>}
      </section>}
    </div>
  );
}
export function ProductCost({ record, catalog, onDependency }: any) {
  const copy = useAdminCopy();
  const { money, translateMessage } = useI18n();
  let content;
  try {
    const candidate = {
      ...catalog,
      products: [
        ...(catalog.products || []).filter((p: any) => p.id !== record.id),
        { ...record, available: true, archived: false },
      ],
    };
    const employee = priceLine(candidate, "employee", {
      productId: record.id,
      quantity: 1,
      selections: record.defaults,
    });
    const pub = priceLine(candidate, "public", {
      productId: record.id,
      quantity: 1,
      selections: record.defaults,
    });
    content = (
      <>
        <strong>
          {copy("Employee", "Karyawan")}: {money(employee.unitPrice)}
        </strong>
        <strong>
          {copy("Public", "Publik")}: {money(pub.unitPrice)}
        </strong>
        <span>
          {copy("Ingredients", "Bahan")}: {money(employee.costs.ingredients)} ·{" "}
          {copy("Equipment", "Peralatan")}: {money(employee.costs.equipment)} ·{" "}
          {copy("Packaging", "Kemasan")}: {money(employee.costs.packaging)}
        </span>
      </>
    );
  } catch (error) {
    content = (
      <span>
        {copy(
          "Complete the recipe to calculate prices.",
          "Lengkapi resep untuk menghitung harga.",
        )}{" "}
        {translateMessage(error)}
        {(() => { const target = validationEditorTarget(error instanceof Error ? error.message : String(error), catalog); return onDependency && target?.record && ['ingredients','packaging','equipment'].includes(target.collection ?? '') ? <button className="text-button" onClick={() => onDependency(target.collection, target.record)}>{copy('Edit', 'Edit')} {target.record.name}</button> : null; })()}
      </span>
    );
  }
  return (
    <aside className="live-cost" aria-live="polite">
      <h3>
        {copy(
          "Price preview · default cup",
          "Pratinjau harga · pilihan default",
        )}
      </h3>
      {content}
    </aside>
  );
}

export function ChangeSelection({ changes, selection, onChange, disabled = false, catalog }: {
  catalog?: any;
  changes: CatalogChange[];
  selection: { collection: string; id: string }[];
  onChange: (next: { collection: string; id: string }[]) => void;
  disabled?: boolean;
}) {
  const copy = useAdminCopy();
  const selected = new Set(selection.map(selectionKey));
  return <fieldset className="save-change-selection" disabled={disabled}>
    <legend>{copy("Choose changes to save or discard", "Pilih perubahan untuk disimpan atau dibuang")}</legend>
    <div className="save-selection-actions">
      <button type="button" className="text-button" onClick={() => onChange(changes.map(changeSelection))}>{copy("Select all", "Pilih semua")}</button>
      <button type="button" className="text-button" onClick={() => onChange([])}>{copy("Clear selection", "Hapus pilihan")}</button>
      <span role="status">{copy(`${selection.length} records selected`, `${selection.length} catatan dipilih`)}</span>
    </div>
    {preparationGroups(changes, catalog).map(row => {
      const items = row.changes.map(changeSelection), keys = new Set(items.map(selectionKey));
      return <label className="save-change-option" key={row.id}>
        <input type="checkbox" checked={items.every(item => selected.has(selectionKey(item)))} onChange={event => onChange(event.target.checked ? [...selection.filter(item => !keys.has(selectionKey(item))), ...items] : selection.filter(item => !keys.has(selectionKey(item))))} />
        <span>{row.name} <small>{row.parts.map(part => part === 'shared' ? copy('Shared details', 'Detail bersama') : part === 'hot' ? copy('Hot', 'Panas') : part === 'iced' ? copy('Iced', 'Dingin') : part === 'recipe' ? copy('Recipe', 'Resep') : part).join(' · ')} · {row.changes.length} {copy('records', 'catatan')}</small></span>
      </label>;
    })}
  </fieldset>;
}

export function DiscardSelectionSummary({ changes, selection }: {
  changes: CatalogChange[];
  selection: { collection: string; id: string }[];
}) {
  const copy = useAdminCopy();
  const { title } = useChangeLabels();
  return <section className="discard-selection-summary">
    <h3>{copy(`${selection.length} pending ${selection.length === 1 ? "change" : "changes"} selected`, `${selection.length} perubahan tertunda dipilih`)}</h3>
    <ul>{selection.map(item => {
      const change = changes.find(change => selectionKey(changeSelection(change)) === selectionKey(item));
      return <li key={selectionKey(item)}>{change ? title(change) : item.id}</li>;
    })}</ul>
  </section>;
}
